import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flame, Plus, X, RefreshCw, Search, Trash2, Pause } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCT = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
        }) + ' CT';
    } catch { return ''; }
};

/**
 * Admin panel for the "Daily Special" featured bet.
 *
 * Flow:
 *  1. Admin picks a sport from the dropdown → we load its current events
 *  2. Admin picks an event + market + outcome
 *  3. Admin writes a 1-line sales pitch ("Cowboys -7 — easy 2-to-1!")
 *  4. Clicks "Make It Live" — auto-deactivates any existing special, fires
 *     in-app notification + chime to every online player
 *
 * History list below shows current + last 25 specials with quick deactivate.
 */
export const DailySpecialAdminPanel = () => {
    const [sports, setSports] = useState([]);
    const [sportKey, setSportKey] = useState('');
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [eventQuery, setEventQuery] = useState('');

    const [selectedEvent, setSelectedEvent] = useState(null);
    const [market, setMarket] = useState('h2h');
    const [outcomeIdx, setOutcomeIdx] = useState(0);
    const [pitch, setPitch] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [rows, setRows] = useState([]);
    const [listLoading, setListLoading] = useState(false);

    // ── Load sports + history once
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/sports`);
                const all = Object.values(data.groups || {}).flat().filter(s => !s.has_outrights);
                setSports(all);
                const preferred = all.find(s => ['americanfootball_nfl', 'basketball_nba', 'icehockey_nhl', 'baseball_mlb'].includes(s.key));
                if (preferred) setSportKey(preferred.key);
                else if (all[0]) setSportKey(all[0].key);
            } catch { toast.error('Could not load sports list'); }
        })();
    }, []);

    // ── Load events whenever sport changes
    useEffect(() => {
        if (!sportKey) return;
        setEventsLoading(true);
        setSelectedEvent(null);
        (async () => {
            try {
                const { data } = await axios.get(`${API}/sports/${sportKey}/odds`);
                setEvents(data.events || []);
            } catch {
                setEvents([]);
                toast.error('Could not load events for that sport');
            }
            setEventsLoading(false);
        })();
    }, [sportKey]);

    const loadHistory = useCallback(async () => {
        setListLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/daily-specials?limit=25`);
            setRows(data.rows || []);
        } catch { /* silent */ }
        setListLoading(false);
    }, []);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    const submit = async () => {
        if (!selectedEvent) { toast.error('Pick an event'); return; }
        const m = selectedEvent.markets?.[market];
        const outcome = m?.outcomes?.[outcomeIdx];
        if (!outcome) { toast.error('Pick a side'); return; }
        if (!pitch.trim()) { toast.error('Write a 1-line pitch — players see it as the headline'); return; }

        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/admin/daily-special`, {
                event_id: selectedEvent.id,
                sport_key: selectedEvent.sport_key || sportKey,
                sport_title: sports.find(s => s.key === sportKey)?.title || '',
                market,
                outcome_name: outcome.name,
                point: outcome.point ?? null,
                odds: outcome.price,
                home_team: selectedEvent.home_team,
                away_team: selectedEvent.away_team,
                commence_time: selectedEvent.commence_time,
                pitch: pitch.trim(),
                expires_at: selectedEvent.commence_time,
                notify: true,
            });
            toast.success(`Live! Notified ${data.notified_count} player${data.notified_count === 1 ? '' : 's'}.`);
            setPitch('');
            setSelectedEvent(null);
            loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create special');
        }
        setSubmitting(false);
    };

    const deactivate = async (id) => {
        try {
            await axios.post(`${API}/admin/daily-special/${id}/deactivate`);
            toast.success('Deactivated');
            loadHistory();
        } catch { toast.error('Could not deactivate'); }
    };

    const remove = async (id) => {
        if (!window.confirm('Delete this special from history?')) return;
        try {
            await axios.delete(`${API}/admin/daily-special/${id}`);
            toast.success('Deleted');
            loadHistory();
        } catch { toast.error('Could not delete'); }
    };

    const filteredEvents = events.filter(e => {
        if (!eventQuery.trim()) return true;
        const q = eventQuery.toLowerCase();
        return e.home_team.toLowerCase().includes(q) || e.away_team.toLowerCase().includes(q);
    });

    const activeOutcomes = selectedEvent?.markets?.[market]?.outcomes || [];

    return (
        <div className="space-y-5" data-testid="daily-special-admin">
            <div className="card-dark p-5" style={{ borderLeft: '3px solid #FF8C00' }}>
                <div className="flex items-center gap-2 mb-1">
                    <Flame className="w-5 h-5 text-[#FF8C00]" />
                    <h3 className="text-lg font-bold text-white">Daily Special — Featured Bet</h3>
                </div>
                <p className="text-xs text-[#A0AAB2] mb-4">
                    Pick one matchup with house-favorable odds, write a 1-line hook, and we'll broadcast it to every active player with a chime.
                    Only one special runs at a time — making a new one auto-replaces the previous.
                </p>

                {/* Step 1 — pick sport */}
                <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-3 mb-3">
                    <div>
                        <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Sport</Label>
                        <select
                            value={sportKey}
                            onChange={(e) => setSportKey(e.target.value)}
                            className="w-full h-9 rounded-md bg-[#0a0a0a] border border-white/10 text-white text-sm px-3"
                            data-testid="ds-sport-select"
                        >
                            {sports.map(s => (
                                <option key={s.key} value={s.key}>{s.title}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Filter events</Label>
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A0AAB2]" />
                            <Input
                                value={eventQuery}
                                onChange={(e) => setEventQuery(e.target.value)}
                                placeholder="Filter by team name…"
                                className="pl-8 h-9 bg-[#0a0a0a] border-white/10 text-white text-sm"
                                data-testid="ds-event-filter"
                            />
                        </div>
                    </div>
                </div>

                {/* Step 2 — pick event */}
                <div className="rounded-lg border border-white/10 bg-[#0a0a0a] max-h-56 overflow-y-auto mb-3">
                    {eventsLoading ? (
                        <div className="text-center text-[11px] text-[#A0AAB2] py-6">Loading events…</div>
                    ) : filteredEvents.length === 0 ? (
                        <div className="text-center text-[11px] text-[#A0AAB2] py-6">
                            No events {eventQuery ? 'match that filter' : 'available'}.
                        </div>
                    ) : filteredEvents.slice(0, 50).map(e => {
                        const sel = selectedEvent?.id === e.id;
                        return (
                            <button
                                key={e.id}
                                type="button"
                                onClick={() => { setSelectedEvent(e); setOutcomeIdx(0); }}
                                className={`w-full text-left px-3 py-2 text-xs border-b border-white/5 last:border-0 ${sel ? 'bg-[#FF8C00]/10 text-white' : 'text-[#A0AAB2] hover:bg-white/[0.03] hover:text-white'}`}
                                data-testid={`ds-event-${e.id}`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-semibold truncate">{e.away_team} @ {e.home_team}</span>
                                    <span className="text-[10px] text-[#666] whitespace-nowrap">{fmtCT(e.commence_time)}</span>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {selectedEvent && (
                    <>
                        {/* Step 3 — market + side */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                            <div>
                                <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Market</Label>
                                <div className="flex gap-1 bg-[#0a0a0a] border border-white/10 rounded-md p-1">
                                    {['h2h', 'spreads', 'totals'].map(m => {
                                        const has = !!selectedEvent.markets?.[m];
                                        const active = market === m;
                                        return (
                                            <button
                                                key={m}
                                                type="button"
                                                disabled={!has}
                                                onClick={() => { setMarket(m); setOutcomeIdx(0); }}
                                                className={`flex-1 text-xs font-bold py-1 rounded ${active ? 'bg-[#FF8C00] text-black' : has ? 'text-[#A0AAB2] hover:text-white' : 'text-[#444] cursor-not-allowed'}`}
                                                data-testid={`ds-market-${m}`}
                                            >
                                                {m === 'h2h' ? 'Moneyline' : m === 'spreads' ? 'Spread' : 'Total'}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Side / outcome</Label>
                                <select
                                    value={outcomeIdx}
                                    onChange={(e) => setOutcomeIdx(parseInt(e.target.value, 10))}
                                    className="w-full h-9 rounded-md bg-[#0a0a0a] border border-white/10 text-white text-sm px-3"
                                    data-testid="ds-outcome-select"
                                >
                                    {activeOutcomes.map((o, i) => {
                                        const pt = o.point != null ? (o.point > 0 ? ` +${o.point}` : ` ${o.point}`) : '';
                                        const odd = o.price > 0 ? `+${o.price}` : `${o.price}`;
                                        return (
                                            <option key={i} value={i}>
                                                {o.name}{pt} ({odd})
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>

                        {/* Step 4 — pitch */}
                        <div className="mb-3">
                            <Label className="text-[11px] text-[#A0AAB2] mb-1 block flex items-center justify-between">
                                <span>Sales pitch (1 line) <span className="text-[#FF8C00]">*</span></span>
                                <span className="text-[10px] text-[#666]">{pitch.length}/200</span>
                            </Label>
                            <Input
                                value={pitch}
                                onChange={(e) => setPitch(e.target.value.slice(0, 200))}
                                placeholder='e.g. Cowboys -7 — easy 2-to-1 tonight!'
                                className="bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="ds-pitch-input"
                            />
                        </div>

                        <Button
                            onClick={submit}
                            disabled={submitting || !pitch.trim()}
                            className="w-full font-bold"
                            style={{ background: 'linear-gradient(135deg,#FF3B30,#FF8C00)', color: 'white' }}
                            data-testid="ds-create-btn"
                        >
                            <Flame className="w-4 h-4 mr-2" />
                            {submitting ? 'Going live…' : 'Make It Live & Notify Players'}
                        </Button>
                    </>
                )}
            </div>

            {/* History */}
            <div className="card-dark p-4">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-white">Current & Recent Specials</h4>
                    <button
                        type="button"
                        onClick={loadHistory}
                        className="text-[#A0AAB2] hover:text-white"
                        title="Refresh"
                        data-testid="ds-refresh"
                    >
                        <RefreshCw className={`w-4 h-4 ${listLoading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
                {rows.length === 0 ? (
                    <p className="text-center text-[11px] text-[#A0AAB2] py-6">No specials yet — create one above.</p>
                ) : (
                    <ul className="space-y-1.5">
                        {rows.map(r => {
                            const oddsLabel = r.odds > 0 ? `+${r.odds}` : `${r.odds}`;
                            return (
                                <li
                                    key={r.id}
                                    className={`rounded-lg p-2.5 border ${r.active ? 'bg-[#FF8C00]/10 border-[#FF8C00]/40' : 'bg-[#0a0a0a] border-white/10'}`}
                                    data-testid={`ds-row-${r.id}`}
                                >
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {r.active && (
                                            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white animate-pulse">
                                                LIVE
                                            </span>
                                        )}
                                        <span className="text-white text-sm font-bold flex-1 truncate">{r.pitch}</span>
                                        <span className={`font-mono text-xs font-bold ${r.odds > 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                            {oddsLabel}
                                        </span>
                                        {r.active ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => deactivate(r.id)}
                                                className="h-7 text-[10px] border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                                                data-testid={`ds-deactivate-${r.id}`}
                                            >
                                                <Pause className="w-3 h-3 mr-1" /> End Early
                                            </Button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => remove(r.id)}
                                                className="text-[#A0AAB2] hover:text-[#FF3B30] p-1"
                                                title="Delete from history"
                                                data-testid={`ds-delete-${r.id}`}
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-[10px] text-[#A0AAB2] mt-0.5 truncate">
                                        {r.away_team} @ {r.home_team}
                                        <span className="mx-1 text-[#444]">·</span>
                                        {r.outcome_name}
                                        {r.point != null && ` ${r.point > 0 ? '+' : ''}${r.point}`}
                                        <span className="mx-1 text-[#444]">·</span>
                                        Kickoff {fmtCT(r.commence_time)}
                                    </p>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default DailySpecialAdminPanel;
